import React from 'react';
import { Link } from 'react-router-dom';
import { Pagination } from 'semantic-ui-react';

const PER_PAGE = 20;
const TYPE_LABELS = { group: 'Group Trip', shared: 'Shared Rental', private: 'Private Tour' };
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'Available', label: 'Available' },
  { value: 'Full', label: 'Full' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Completed', label: 'Completed' }
];
// Private tours: filter by published vs unpublished (simple labels)
const STATUS_OPTIONS_PRIVATE = [
  { value: '', label: 'All' },
  { value: 'Available', label: 'Published' },
  { value: 'Closed', label: 'Unpublished' }
];

export default function AdminListingTable({
  type,
  title,
  rows = [],
  meta = {},
  loading = false,
  fetchError = false,
  onRetry,
  filters = {},
  onFilterChange,
  onPageChange,
  onPublishedToggle,
  onView,
  onEdit,
  onDelete,
  addHref,
  backHref,
  deleteDisabled = false,
  editHref,
  viewHref
}) {
  const pag = meta.pagination || meta || {};
  const totalPages = pag.total_pages || 1;
  const currentPage = pag.current_page || 1;
  const hasPagination = totalPages > 1;

  const handleSearch = (e) => {
    const v = e.target.value;
    onFilterChange && onFilterChange({ ...filters, search: v });
  };
  const handleStatusFilter = (e) => {
    const v = e.target.value;
    onFilterChange && onFilterChange({ ...filters, status: v });
  };
  const handlePublishedFilter = (e) => {
    const v = e.target.value;
    onFilterChange && onFilterChange({ ...filters, published: v });
  };
  const handleVendorFilter = (e) => {
    const v = e.target.value;
    onFilterChange && onFilterChange({ ...filters, vendor_id: v });
  };
  const handleDateFrom = (e) => {
    onFilterChange && onFilterChange({ ...filters, date_from: e.target.value });
  };
  const handleDateTo = (e) => {
    onFilterChange && onFilterChange({ ...filters, date_to: e.target.value });
  };

  const showSpots = type !== 'private';
  const showStatus = true;
  const statusOptions = type === 'private' ? STATUS_OPTIONS_PRIVATE : STATUS_OPTIONS;
  const vendorOptions = filters.vendorOptions || [];
  const showVendorFilter = vendorOptions.length > 0;

  return (
    <div className="admin-listing-table">
      {backHref && (
        <Link to={backHref} className="btn btn-outline-secondary btn-sm mb-3">← Back to Dashboard</Link>
      )}
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <h3 className="title mb-0">{title}</h3>
        {addHref && (
          <Link to={addHref} className="btn bg-none color-accent">Add {TYPE_LABELS[type] || type}</Link>
        )}
      </div>

      {/* Filters */}
      <div className="admin-listing-table__filters mb-3 d-flex flex-wrap align-items-center gap-2">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Search by title"
          value={filters.search || ''}
          onChange={handleSearch}
          onBlur={() => onFilterChange && onFilterChange({ ...filters })}
          style={{ width: 180 }}
        />
        {showStatus && (
          <select
            className="form-control form-control-sm"
            value={filters.status || ''}
            onChange={handleStatusFilter}
            style={{ width: 'auto' }}
          >
            {statusOptions.map((o) => (
              <option key={o.value || 'all'} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
        <select
          className="form-control form-control-sm"
          value={filters.published || ''}
          onChange={handlePublishedFilter}
          style={{ width: 'auto' }}
        >
          <option value="">Published: All</option>
          <option value="true">Published: Yes</option>
          <option value="false">Published: No</option>
        </select>
        {type !== 'private' && (
          <>
            <input
              type="date"
              className="form-control form-control-sm"
              placeholder="From"
              value={filters.date_from || ''}
              onChange={handleDateFrom}
              style={{ width: 140 }}
            />
            <input
              type="date"
              className="form-control form-control-sm"
              placeholder="To"
              value={filters.date_to || ''}
              onChange={handleDateTo}
              style={{ width: 140 }}
            />
          </>
        )}
        {showVendorFilter && (
          <select
            className="form-control form-control-sm"
            value={filters.vendor_id || ''}
            onChange={handleVendorFilter}
            style={{ width: 'auto', minWidth: 160 }}
          >
            <option value="">Vendor: All</option>
            {vendorOptions.map((v) => (
              <option key={v.id} value={v.id}>{v.company_name || v.name || `Vendor ${v.id}`}</option>
            ))}
          </select>
        )}
      </div>

      {fetchError && (
        <div className="alert alert-warning mb-3">
          Could not load list. {onRetry && <button type="button" className="btn btn-sm btn-outline-secondary ml-2" onClick={onRetry}>Retry</button>}
        </div>
      )}

      {loading && rows.length === 0 && <p className="text-muted">Loading…</p>}

      {!loading && rows.length === 0 && !fetchError && (
        <p className="text-muted">No listings. Add one to get started.</p>
      )}

      {rows.length > 0 && (
        <>
          <div className="table-responsive">
            <table className="table table-bordered table-hover table-sm admin-listing-table__table">
              <thead className="thead-light">
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  {type !== 'private' && <th>Start Date</th>}
                  {type !== 'private' && <th>End Date</th>}
                  <th>Status</th>
                  {showSpots && <th>Spots Left</th>}
                  <th>Price</th>
                  <th>Published</th>
                  <th>Vendor</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.title}</td>
                    <td>{TYPE_LABELS[row.type] || row.type}</td>
                    {type !== 'private' && <td>{row.start_date_display}</td>}
                    {type !== 'private' && <td>{row.end_date_display}</td>}
                    <td>
                      <span className={`badge badge-${row.status === 'Available' ? 'success' : row.status === 'Full' ? 'danger' : row.status === 'Completed' ? 'info' : 'secondary'}`}>
                        {row.status}
                      </span>
                    </td>
                    {showSpots && <td>{row.spots_left !== '—' ? `${row.spots_left}${row.max_group_size !== '—' ? ` / ${row.max_group_size}` : ''}` : '—'}</td>}
                    <td>{row.price}</td>
                    <td>
                      {onPublishedToggle ? (
                        <button
                          type="button"
                          className={`btn btn-sm ${row.published ? 'btn-success' : 'btn-outline-secondary'}`}
                          onClick={() => onPublishedToggle(row)}
                          title={row.published ? 'Published' : 'Unpublished'}
                        >
                          {row.published ? 'Yes' : 'No'}
                        </button>
                      ) : (
                        <span className={row.published ? 'text-success' : 'text-muted'}>{row.published ? 'Yes' : 'No'}</span>
                      )}
                    </td>
                    <td>{row.vendor}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {viewHref ? (
                          <Link to={viewHref(row)} className="btn btn-sm btn-outline-primary">View</Link>
                        ) : onView ? (
                          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onView(row)}>View</button>
                        ) : null}
                        {editHref ? (
                          <Link to={editHref(row)} className="btn btn-sm btn-outline-secondary">Edit</Link>
                        ) : onEdit ? (
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onEdit(row)}>Edit</button>
                        ) : null}
                        {onDelete && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => onDelete(row)}
                            disabled={deleteDisabled}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasPagination && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination
                activePage={currentPage}
                totalPages={totalPages}
                onPageChange={(e, { activePage }) => onPageChange && onPageChange(activePage)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export { PER_PAGE, TYPE_LABELS, STATUS_OPTIONS };
